import { Injectable, Signal, signal } from '@angular/core';
import {
  defer,
  finalize,
  Observable,
  ReplaySubject,
  Subscription,
  switchMap,
  take,
  tap,
  timer,
} from 'rxjs';

export type ServerErrorPhase = 'idle' | 'counting' | 'retrying' | 'failed';

/** Delay before the single automatic retry of failed (5xx) requests. */
export const RETRY_DELAY_SECONDS = 30;

type RetryOutcome = 'success' | 'failure' | 'abandoned';

/**
 * State machine behind the blocking server-error modal. The first 5xx starts one
 * shared countdown; every failed request retries once when it elapses. All retries
 * succeeding closes the modal; any retry failing parks the app in 'failed'.
 */
@Injectable({
  providedIn: 'root',
})
export class ServerErrorRetryService {
  private readonly _phase = signal<ServerErrorPhase>('idle');
  public readonly phase: Signal<ServerErrorPhase> = this._phase.asReadonly();

  private readonly _secondsLeft = signal<number>(RETRY_DELAY_SECONDS);
  public readonly secondsLeft: Signal<number> = this._secondsLeft.asReadonly();

  private readonly _errors = signal<readonly unknown[]>([]);
  /** Every failure of the current cycle (originals and failed retries), for dev diagnostics. */
  public readonly errors: Signal<readonly unknown[]> = this._errors.asReadonly();

  private retryTick$ = new ReplaySubject<void>(1);
  private countdownSub: Subscription | null = null;
  private pendingRetries = 0;
  private cycleFailed = false;

  public isFailed(): boolean {
    return this._phase() === 'failed';
  }

  /**
   * Registers a failed request and replays it via `retry` once the shared countdown
   * elapses. The returned stream is the retry's outcome: its response on success,
   * its error on failure — the original failure never reaches the caller.
   */
  public retryAfterCountdown<T>(retry: () => Observable<T>, cause?: unknown): Observable<T> {
    return defer((): Observable<T> => {
      this.register(cause);
      let settled = false;
      const settle = (outcome: RetryOutcome): void => {
        if (!settled) {
          settled = true;
          this.settle(outcome);
        }
      };

      return this.retryTick$.pipe(
        take(1),
        switchMap(retry),
        tap({
          complete: (): void => settle('success'),
          error: (error: unknown): void => {
            this.recordError(error);
            settle('failure');
          },
        }),
        // Unsubscribed before an outcome (caller destroyed) → drop the registration.
        finalize((): void => settle('abandoned')),
      );
    });
  }

  private register(cause: unknown): void {
    this.pendingRetries += 1;
    if (this._phase() === 'idle') {
      this.startCycle();
    }
    this.recordError(cause);
  }

  private recordError(error: unknown): void {
    if (error !== undefined) {
      this._errors.update((errors: readonly unknown[]): unknown[] => [...errors, error]);
    }
  }

  private startCycle(): void {
    this.cycleFailed = false;
    this.retryTick$ = new ReplaySubject<void>(1);
    this._errors.set([]);
    this._secondsLeft.set(RETRY_DELAY_SECONDS);
    this._phase.set('counting');

    this.countdownSub = timer(1000, 1000)
      .pipe(take(RETRY_DELAY_SECONDS))
      .subscribe((elapsed: number): void => {
        const left: number = RETRY_DELAY_SECONDS - (elapsed + 1);
        this._secondsLeft.set(left);
        if (left === 0) {
          this._phase.set('retrying');
          this.retryTick$.next();
        }
      });
  }

  private settle(outcome: RetryOutcome): void {
    this.pendingRetries -= 1;
    if (outcome === 'failure') {
      this.cycleFailed = true;
      this._phase.set('failed');
    }
    if (this.pendingRetries === 0 && !this.cycleFailed) {
      this.resetToIdle();
    }
  }

  private resetToIdle(): void {
    this.countdownSub?.unsubscribe();
    this.countdownSub = null;
    this._phase.set('idle');
    this._secondsLeft.set(RETRY_DELAY_SECONDS);
  }
}
